import { RootStateOrAny, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { AnalyticsQueryParams, QueryParamUrlObject } from '@candu-ai/analytics/types';
import { toEndOfDayTimestamp, toSecondTimeStamp } from '@candu-ai/analytics/utils';

import { AnalyticsSearchState } from './reducer';

import { encodeFilters } from '../filtersMarshalling';
import { Filter, FilterOperations, FilterTypes } from '../types';

const rootSelector = (root: RootStateOrAny) => root['analytics/search'];

export const selectIsAddingFilter = createSelector(
  rootSelector,
  ({ isAddingFilter }) => isAddingFilter,
);
export const selectEditingFilter = createSelector(
  rootSelector,
  ({ editingFilter }) => editingFilter,
);
export const selectWhitelistedSegmentIds = createSelector(
  rootSelector,
  ({ whitelistedSegmentIds }) => whitelistedSegmentIds,
);
export const selectFilters = createSelector(rootSelector, ({ filters }) => filters);
export const useIsAddingFilter = (): boolean => useSelector(selectIsAddingFilter);
export const useEditingFilter = (): Filter | undefined => useSelector(selectEditingFilter);
export const useFilters = (): Filter[] => useSelector(selectFilters);
export const useAnalyticsSearchState: () => AnalyticsSearchState = () => useSelector(rootSelector);
export const useWhitelistedSegmentIds: () => string[] = () =>
  useSelector(selectWhitelistedSegmentIds);

export const useAnalyticsQueryParams = (additionalParams: object = {}): QueryParamUrlObject => {
  const { shortTimeInterval, dateRange, filters } = useAnalyticsSearchState();
  const dateParams =
    shortTimeInterval === 'custom' && dateRange
      ? {
          [AnalyticsQueryParams.Timestamp]: toSecondTimeStamp(dateRange?.[0]),
          [AnalyticsQueryParams.UpperTimestamp]: toEndOfDayTimestamp(dateRange?.[1]),
        }
      : { [AnalyticsQueryParams.TimeInterval]: shortTimeInterval };

  const filtersParams =
    filters.length > 0 ? { [AnalyticsQueryParams.Filters]: encodeFilters(filters) } : {};

  // @ts-expect-error
  return {
    ...dateParams,
    ...filtersParams,
    ...additionalParams,
  };
};

export const useAvaliableSegmentIds = (segmentIds: string[]): string[] => {
  const rules = useFilters();
  const segmentRules = rules.filter((rule) => rule.type === FilterTypes.SegmentId);
  if (segmentRules.length < 1) {
    return segmentIds;
  }

  let avaliableSegmentIds = [...segmentIds];
  segmentRules.forEach((rule) => {
    if (rule.operation.type === FilterOperations.Equals) {
      avaliableSegmentIds = [...avaliableSegmentIds].filter((s) => s === rule.operation.operand);
    } else if (rule.operation.type === FilterOperations.EqualsNot) {
      avaliableSegmentIds = [...avaliableSegmentIds].filter((s) => s === rule.operation.operand);
    } else if (rule.operation.type === FilterOperations.OneOf) {
      avaliableSegmentIds = [...avaliableSegmentIds].filter((s) =>
        rule.operation.operand.includes(s),
      );
    } else if (rule.operation.type === FilterOperations.NotOneOf) {
      avaliableSegmentIds = [...avaliableSegmentIds].filter(
        (s) => !rule.operation.operand.includes(s),
      );
    } else {
      throw new Error(`Unknown rule ${rule.operation.operand}`);
    }
  });

  return avaliableSegmentIds;
};
